import * as React from "react";
import { cn } from "@/zapi-frontend/shared/lib/utils";

import { Tabs, TabsList, TabsTrigger } from "@libs/zapi-ui/ds/components/tabs";
import { stickyOffset, useStuck } from "@/zapi-frontend/shared/hooks/useStuck";

type ContextTab = { id: string; label: string; count?: number };

const STICKY_ROW =
  "sticky top-(--app-header-height) z-30 -mx-3 px-3 transition-[background-color] duration-300 ease-out data-stuck:bg-canvas/75 data-stuck:backdrop-blur-xl data-stuck:backdrop-saturate-150";

// A solid 8px band keeps pinned content below off the row and hides what scrolls behind it.
const STICKY_ROW_BAND =
  "pb-2 before:absolute before:inset-x-0 before:bottom-0 before:h-2 before:bg-canvas before:opacity-0 before:transition-opacity before:duration-300 data-stuck:before:opacity-100";

// Pinned, the row fades into the content under it instead of cutting it off.
const STICKY_ROW_FADE =
  "after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-6 after:bg-linear-to-b after:from-canvas/75 after:to-transparent after:opacity-0 after:transition-opacity after:duration-300 data-stuck:after:opacity-100";

export const stickyRowClassName = cn(STICKY_ROW, STICKY_ROW_BAND, STICKY_ROW_FADE);

function ContextTabs({
  tabs,
  value,
  onValueChange,
  sticky = false,
  actions,
  className
}: {
  tabs: ContextTab[];
  value: string;
  onValueChange: (id: string) => void;
  sticky?: boolean;
  actions?: React.ReactNode;
  className?: string;
}) {
  const { ref: stickyRef, stuck, naturalTop } = useStuck<HTMLDivElement>(sticky);
  const listRef = React.useRef<HTMLDivElement>(null);
  const [fade, setFade] = React.useState<"start" | "end" | "both" | undefined>();

  // Fade whichever edge has tabs scrolled past it. scrollLeft runs negative in RTL,
  // so the distance from the start is its absolute value.
  React.useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const measure = () => {
      const overflow = list.scrollWidth - list.clientWidth;
      const fromStart = Math.abs(list.scrollLeft);
      const start = fromStart > 1;
      const end = overflow - fromStart > 1;
      setFade(start && end ? "both" : start ? "start" : end ? "end" : undefined);
    };
    measure();
    list.addEventListener("scroll", measure, { passive: true });
    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => {
      list.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [tabs.length]);

  const firstRender = React.useRef(true);
  React.useEffect(() => {
    const list = listRef.current;
    const active = list?.querySelector<HTMLElement>("[data-slot=tabs-trigger][data-active]");
    // On mount only the row scrolls, so opening a page never moves the window.
    if (firstRender.current) {
      firstRender.current = false;
      if (!list || !active) return;
      const row = list.getBoundingClientRect();
      const tab = active.getBoundingClientRect();
      if (tab.left < row.left || tab.right > row.right) {
        list.scrollLeft += tab.left + tab.width / 2 - (row.left + row.width / 2);
      }
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    active?.scrollIntoView({ block: "nearest", inline: "nearest", behavior: reduced ? "auto" : "smooth" });
  }, [value]);

  // Switching tabs while pinned brings the new tab's content up from its top, rather
  // than leaving the reader part-way down a page that is now a different length.
  const select = (next: string) => {
    const row = stickyRef.current;
    if (stuck && row) {
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      window.scrollTo({ top: naturalTop() - stickyOffset(row), behavior: reduced ? "auto" : "smooth" });
    }
    onValueChange(next);
  };

  return (
    <div
      ref={stickyRef}
      data-slot="context-tabs"
      data-sticky={sticky || undefined}
      data-stuck={stuck || undefined}
      className={cn(
        "relative flex min-w-0 items-end gap-2",
        // The negative margin keeps the resting layout unchanged under the band's padding.
        sticky && cn(STICKY_ROW, STICKY_ROW_BAND, "pointer-events-none -mb-2 *:pointer-events-auto"),
        STICKY_ROW_FADE,
        className
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          "absolute inset-x-0 bottom-0 h-px bg-foreground/10 transition-opacity duration-300",
          sticky && "inset-x-3 bottom-2"
        )}
      />
      <Tabs
        value={value}
        onValueChange={(next) => typeof next === "string" && select(next)}
        className="relative min-w-0"
      >
        <TabsList
          ref={listRef}
          variant="line"
          data-fade={fade}
          className={cn(
            "h-11! max-w-full justify-start gap-5 overflow-x-auto overflow-y-hidden p-0 [scrollbar-width:thin]",
            "[--fade-to:to_right] rtl:[--fade-to:to_left]",
            "data-[fade=start]:[mask-image:linear-gradient(var(--fade-to),transparent,black_2.5rem)]",
            "data-[fade=end]:[mask-image:linear-gradient(var(--fade-to),black_calc(100%-2.5rem),transparent)]",
            "data-[fade=both]:[mask-image:linear-gradient(var(--fade-to),transparent,black_2.5rem,black_calc(100%-2.5rem),transparent)]"
          )}
        >
          {tabs.map((tab) => (
            <span key={tab.id} className="group/tab relative flex h-full flex-none">
              <TabsTrigger
                value={tab.id}
                className="typo-label-md h-full flex-none gap-1.5 rounded-none px-0.5 text-muted-foreground after:bottom-0! data-active:text-foreground"
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="typo-caption min-w-5 rounded-full bg-muted px-1.5 text-center text-muted-foreground tabular-nums">
                    {tab.count}
                  </span>
                )}
              </TabsTrigger>
            </span>
          ))}
        </TabsList>
      </Tabs>
      {actions && (
        <div data-slot="context-tabs-actions" className="ms-auto flex h-11 shrink-0 items-center gap-1">
          {actions}
        </div>
      )}
    </div>
  );
}

export { ContextTabs, type ContextTab };
